import { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setAuthToken } from '../common';
import PmsUrlConfig from '../../urlConfig';
import { viewChat } from '../appointment/appointmentFunctions';
import PatientAppointmentDetailsHeader from './components/PatientAppointmentDetailsHeader';
import PatientAppointmentBasicInfo from './components/PatientAppointmentBasicInfo';
import PatientAppointmentContactInfo from './components/PatientAppointmentContactInfo';
import PatientAppointmentPracticesTable from './components/PatientAppointmentPracticesTable';

export default function PatientAppointmentDetails({ 
    showApptDetails, 
    handleApptDetailsClose, 
    selectedAppointment, 
    loggedInUser, 
    dispatch, 
    serviceTypes 
}) {
    const chatDetails = useSelector(state => state.chatDetails);
    const matrixClient = chatDetails.matrixClient;
    const token = useSelector(state => state.loginDetails?.jwtToken || '');
    const appointmentId = selectedAppointment?.appointment_basic_details?._id;
    const savedSearchRadius = selectedAppointment?.search_radius_miles;
    const [searchRadius, setSearchRadius] = useState(savedSearchRadius == null ? '' : String(savedSearchRadius));
    const [isUpdating, setIsUpdating] = useState(false);
    const isUpdatingRef = useRef(false);

    useEffect(() => {
        setSearchRadius(savedSearchRadius == null ? '' : String(savedSearchRadius));
    }, [appointmentId, savedSearchRadius]);

    const handleCancel = () => {
        if (isUpdatingRef.current) {
            return;
        }
        setSearchRadius(savedSearchRadius == null ? '' : String(savedSearchRadius));
        handleApptDetailsClose();
    };

    const handleUpdate = async () => {
        const selectedRadius = Number(searchRadius);
        if (isUpdatingRef.current) {
            return;
        }
        if (!appointmentId || ![5, 10, 15].includes(selectedRadius)) {
            toast.error('Select a valid search radius.');
            return;
        }

        isUpdatingRef.current = true;
        setIsUpdating(true);
        setAuthToken(token);
        try {
            await Axios.post(PmsUrlConfig.BOS_UPDATE_APPOINTMENT_URL, {
                AppointmentId: appointmentId,
                search_radius_miles: selectedRadius,
            });
            dispatch({
                type: 'selectAppointment',
                payload: { ...selectedAppointment, search_radius_miles: selectedRadius }
            });
            handleApptDetailsClose();
        } catch (error) {
            const message = error.response?.data?.error_code?.description || 'Unable to update appointment.';
            toast.error(message);
        } finally {
            isUpdatingRef.current = false;
            setIsUpdating(false);
        }
    };

    const handlePracticeChat = (appointment, practice) => {
        console.log('=== PatientAppointmentDetails handlePracticeChat ===');
        console.log('Appointment:', appointment);
        console.log('Practice:', practice);
        console.log('Matrix Client:', matrixClient);
        
        // Select the appointment in Redux
        dispatch({
            type: 'selectAppointment',
            payload: appointment
        });
        
        // Handle Matrix room operations if client is available
        if (matrixClient) {
            console.log('Matrix client available, proceeding with room sync...');
            
            // Import getRoomInitialSync here to avoid circular dependency
            import('../matrix/matrixFuntions').then(({ getRoomInitialSync }) => {
                getRoomInitialSync(practice.synapse_room_id, matrixClient, dispatch);
                
                if (matrixClient.store.rooms) {
                    const selectedRoom = matrixClient.store.rooms[practice.synapse_room_id];
                    console.log('Selected room from store:', selectedRoom);
                    dispatch({
                        type: 'setSelectedChatRoom',
                        payload: selectedRoom
                    });
                    dispatch({
                        type: 'setSynapseRoomID',
                        payload: practice.synapse_room_id
                    });
                    
                    // Import markRead here to avoid circular dependency
                    import('../matrix/matrixFuntions').then(({ markRead }) => {
                        markRead(loggedInUser, matrixClient, selectedRoom);
                    });
                } else {
                    console.log('Matrix client store rooms not available');
                }
            });
        } else {
            console.log('Matrix client not available in chatDetails');
        }
        
        // Open chat modal
        try {
            console.log('About to call viewChat function...');
            viewChat(appointment, loggedInUser, practice, dispatch);
            console.log('viewChat function called successfully');
        } catch (error) {
            console.error('Error calling viewChat function:', error);
        }
        
        // Close the appointment details modal
        handleApptDetailsClose();
    };

    return (
        <div>
            <Modal 
                id="appt-details" 
                show={showApptDetails} 
                onHide={handleCancel}
                size='xl'
                dialogClassName="w-full max-w-7xl mx-auto"
                contentClassName="bg-white rounded-lg"
            >
                <Modal.Header closeButton className="border-b border-gray-200 px-6 py-4">
                    <Modal.Title className="text-2xl font-semibold text-gray-800">
                        View Appointment Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-6 py-4">
                    {selectedAppointment && (
                        <>
                            <PatientAppointmentDetailsHeader selectedAppointment={selectedAppointment} />
                            
                            <div className="space-y-6">
                                <PatientAppointmentBasicInfo 
                                    selectedAppointment={selectedAppointment} 
                                    serviceTypes={serviceTypes} 
                                    searchRadius={searchRadius}
                                    onSearchRadiusChange={setSearchRadius}
                                    isUpdating={isUpdating}
                                />
                                
                                <PatientAppointmentContactInfo selectedAppointment={selectedAppointment} />
                                
                                <PatientAppointmentPracticesTable 
                                    selectedAppointment={selectedAppointment}
                                    loggedInUser={loggedInUser}
                                    dispatch={dispatch}
                                    matrixClient={matrixClient}
                                    onPracticeChat={handlePracticeChat}
                                />
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-t border-gray-200 px-6 py-4">
                    <div className="flex justify-end gap-3 w-full">
                        <button 
                            type="button"
                            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleCancel}
                            disabled={isUpdating}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="px-6 py-2 bg-teal-700 text-white font-medium rounded-md hover:bg-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleUpdate}
                            disabled={isUpdating || !searchRadius}
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}